import { atom } from "jotai";
import { atomFamily, atomWithRefresh } from "jotai/utils";

import { Logger } from "@moment/logging";

import { type AwsSsoStatus, awsApi } from "../_interop/aws";
import { $queryState, $queryStateKeysByDocument } from "../request-page/query-state.atom";
import type { QueryError } from "../request-page/request-page.types";

const logger = new Logger("aws");

const emptyStatus: AwsSsoStatus = {
	logged_in: false,
	account_id: null,
	account_name: null,
	role_name: null,
	sso_region: null,
	default_region: null,
	access_token_expires_at: null,
	role_credentials_expires_at: null,
	credential_source: null,
};

export const $awsSsoStatus = atomWithRefresh(async () => {
	const api = awsApi();
	try {
		return await api.ssoStatus();
	} catch (error) {
		logger.error("Failed to get AWS login status", { cause: error });
		return emptyStatus;
	}
});
$awsSsoStatus.debugLabel = "awsSsoStatus";

export const $selectedAwsProfile = atom(async (get) => {
	const status = await get($awsSsoStatus);
	if (!status.logged_in || !status.account_id || !status.role_name) {
		return null;
	}

	return `${status.account_id}:${status.role_name}`;
});
$selectedAwsProfile.debugLabel = "selectedAwsProfile";

export const $awsLoginDialogOpen = atom(false as boolean);
$awsLoginDialogOpen.debugLabel = "awsLoginDialogOpen";

export const $selectedAwsRegion = atom(async (get) => {
	const status = await get($awsSsoStatus);
	return status.sso_region ?? null;
});
$selectedAwsRegion.debugLabel = "selectedAwsRegion";

/**
 * Scans all query state entries for a document and returns the first
 * AWS_LOGIN_REQUIRED error found, or null. Works across all request pages
 * so the environment popover tooltip shows even when viewing a non-request page.
 */
export const $awsLoginRequiredError = atomFamily((documentId: string) => {
	const errorAtom = atom((get): QueryError | null => {
		const keys = get($queryStateKeysByDocument(documentId));
		for (const key of keys) {
			const state = get($queryState(key));
			if (state.error?.code === "AWS_LOGIN_REQUIRED") {
				return state.error;
			}
		}
		return null;
	});
	errorAtom.debugLabel = `awsLoginRequiredError/${documentId}`;
	return errorAtom;
});
